import { Component, OnInit, OnDestroy, ViewEncapsulation, HostListener, Renderer2, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { ChatService } from 'app/modules/admin/chat/chat.service';
import { MobileDetectionService } from 'app/core/services/mobile-detection.service';
import { MailboxService } from 'app/modules/admin/mailbox/mailbox.service';
import { AuthService } from 'app/core/auth/auth.service';
import { OrdersService } from 'app/modules/admin/orders/orders.service';
import {
    operatesAsIndependentSales,
    operatesAsTrader,
} from 'app/core/user/domain-roles';

@Component({
    selector: 'mobile-layout',
    templateUrl: './mobile.component.html',
    styleUrls: ['./mobile.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatBadgeModule,
    ],
})
export class MobileLayoutComponent implements OnInit, OnDestroy {
    activeTab: 'orders' | 'delivery' | 'email' | 'chat' | 'pricing' | 'aiQuote' | 'quotes' | 'mockup' | 'notes' = 'orders';
    unreadChatCount: number = 0;
    unreadEmailCount: number = 0;
    /**
     * Pending deliveries badge count. Mirrors the chat / email unread
     * counts in the bottom nav so admins/production users see at a
     * glance that orders are waiting to go out without first having
     * to tap the Delivery tab.
     */
    pendingDeliveryCount: number = 0;
    isStandalone: boolean = false;
    isKeyboardOpen: boolean = false;
    
    // User role - determines if delivery tab is shown
    showDeliveryTab: boolean = false;
    /**
     * Whether the Email tab is visible. Email is shown to internal
     * staff (Admin / Sales / Trader / Production), but NOT to external
     * "customer" users — their mobile nav is intentionally limited to
     * Orders, Pricing, Design Studio (Mockup), Chat and Sign Out.
     */
    showEmailTab: boolean = true;
    /**
     * Whether the current user is an external customer. Customers get a
     * trimmed-down mobile experience (no Email / Delivery / Quotes).
     */
    isCustomer: boolean = false;
    /**
     * Sales / Trader bottom-nav layout. These quoting roles get a bespoke
     * bottom nav: Orders, Pricing, Mockup Studio and Notes (Quotes) as
     * primary tabs, with Chat + Email demoted into the overflow sheet.
     * Mockup Studio — which lives in the overflow for every other role —
     * is promoted to a first-class bottom tab here because it's central
     * to the sales/trader quoting flow.
     */
    usesQuotingLayout: boolean = false;
    /**
     * Admin / Production "operations" bottom-nav layout. These fulfilment
     * roles get Orders, Delivery, Chat and Notes as primary tabs, with
     * Email, Pricing, Design Studio (Mockup) and Sign Out tucked into the
     * overflow sheet — keeping the bottom strip focused on the day-to-day
     * production workflow.
     */
    usesOpsLayout: boolean = false;
    /**
     * Whether the Pricing tab is visible. The mobile pricing flow lets
     * Sales / Admin / Trader users quote prospects on the go (with an
     * email + SMS send + internal note save), so we restrict the tab
     * to those roles. Other roles (Production / Printer) don't quote.
     */
    showPricingTab: boolean = false;
    /** Voice pricing uses the backend's stricter Admin/Trader/Sales/Assistant role list. */
    showAiQuoteTab: boolean = false;
    /**
     * Whether the "Quotes & Notes" tab is visible. This page surfaces the
     * user's saved pricing quotes + notepad snapshots (the same records
     * the desktop quick-chat Archive panel shows). It's primarily a Sales
     * tool — reps quote on the go and need a dedicated place to find
     * those saved quotes again — so we gate it to the non-admin quoting
     * roles (Sales / Trader). Admin already runs a fuller bottom nav and
     * has the Archive panel on desktop, so we don't add a 6th tab there.
     */
    showQuotesTab: boolean = false;

    /**
     * Mockup Studio (Gemini mockup generator) lives in the overflow
     * sheet rather than the bottom strip so the 5-tab limit holds.
     * Available to the quoting roles (Admin / Sales / Trader) — the same
     * set that gets the Pricing tool.
     */
    showMockupTab: boolean = false;
    
    // PWA Install prompt
    deferredPrompt: any = null;
    showInstallButton: boolean = false;

    private _unsubscribeAll = new Subject<void>();
    private _initialViewportHeight: number = 0;
    private _viewportResizeHandler: (() => void) | null = null;
    /**
     * Polling timer for the Delivery badge count. Refreshes every
     * 60 seconds so the badge stays roughly current without spamming
     * the API. Cleared in ngOnDestroy and restarted on route changes
     * that land on / leave the delivery tab.
     */
    private _deliveryPollTimer: any = null;

    constructor(
        private _router: Router,
        private _chatService: ChatService,
        private _mobileDetection: MobileDetectionService,
        private _mailboxService: MailboxService,
        private _authService: AuthService,
        private _ordersService: OrdersService,
        private _renderer: Renderer2,
        @Inject(PLATFORM_ID) private _platformId: Object
    ) {}

    /**
     * Capture the beforeinstallprompt event
     */
    @HostListener('window:beforeinstallprompt', ['$event'])
    onBeforeInstallPrompt(event: any): void {
        // Prevent the mini-infobar from appearing on mobile
        event.preventDefault();
        // Stash the event so it can be triggered later
        this.deferredPrompt = event;
        // Show the install button
        this.showInstallButton = true;
        console.log('📱 PWA install prompt available');
    }

    /**
     * Handle app installed event
     */
    @HostListener('window:appinstalled', ['$event'])
    onAppInstalled(event: any): void {
        console.log('📱 PWA was installed');
        this.showInstallButton = false;
        this.deferredPrompt = null;
    }

    ngOnInit(): void {
        // Set initial active tab based on current route
        this._updateActiveTab(this._router.url);

        // Listen to route changes
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll)
        ).subscribe((event: NavigationEnd) => {
            this._updateActiveTab(event.url);
        });

        // -----------------------------------------------------------------
        // Pre-warm ChatService as soon as the user lands in the mobile
        // shell, mirroring what the desktop quick-chat layout does via
        // its always-mounted <app-chat-drawer>. Without this, no Twilio
        // bootstrap happens until the user actually taps the CHAT tab,
        // and every cold tap pays the full token-fetch + websocket
        // handshake + getSubscribedConversations cost (~1.5-3.5s) in
        // front of them as a black screen. Pulling profile -> initialize
        // up here lets it run in the background while the user is
        // looking at Orders / Email / etc., so by the time they hit
        // CHAT the chats$ BehaviorSubject is already populated and
        // the mobile-chat auto-select fires on the first poll tick.
        // -----------------------------------------------------------------
        this._chatService.getProfile()
            .pipe(take(1), takeUntil(this._unsubscribeAll))
            .subscribe(profile => {
                if (profile?.id) {
                    this._chatService
                        .initialize(profile.id)
                        .catch(err =>
                            console.error(
                                '[MobileLayout] ChatService init failed:',
                                err
                            )
                        );
                }
            });

        // Subscribe to chat unread count
        this._chatService.chats$.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe(chats => {
            if (chats) {
                this.unreadChatCount = chats.reduce((count, chat) => 
                    count + (chat.unreadCount || 0), 0
                );
            }
        });

        // Check standalone mode
        this._mobileDetection.isStandalone$.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe(isStandalone => {
            this.isStandalone = isStandalone;
        });

        // Check if user previously dismissed install prompt
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (dismissed) {
            this.showInstallButton = false;
        }

        // Check user role for delivery tab visibility
        this._checkUserRole();

        // Setup keyboard detection for mobile
        this._setupKeyboardDetection();

        // Subscribe to email unread count
        this._mailboxService.unreadCount$.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe(count => {
            this.unreadEmailCount = count;
        });

        // Start email unread count polling only when Nylas is linked
        if (this._mailboxService.hasNylasGrant()) {
            this._mailboxService.startUnreadCountPolling();
        }
    }

    /**
     * Check if user should see the delivery tab (admin or production roles)
     */
    private _checkUserRole(): void {
        try {
            const userData = JSON.parse(localStorage.getItem('userData') || '{}');
            const userRole = userData?.role?.toLowerCase() || '';
            const isAdmin = userRole === 'admin' || userRole === 'administrator';
            // NOTE: gate on the role string ONLY. We deliberately do NOT
            // treat a populated `production_manager_email` as "is a
            // production user" — the backend assigns every user a PM
            // email (or a default fallback), so Sales / Traders also
            // carry that field and were wrongly seeing the Delivery tab.
            const isProduction = userRole === 'production' || userRole === 'production manager';
            const isAssistant = userRole === 'assistant';
            const isBusinessAssistant =
                isAssistant && !!userData?.business_account_id;
            const isSales =
                userRole === 'sales' || operatesAsIndependentSales(userData);
            const isTrader =
                userRole === 'trader' || operatesAsTrader(userData);
            // External customers get a trimmed mobile nav: Orders,
            // Pricing, Design Studio (Mockup), Chat and Sign Out only.
            const isCustomer = userRole === 'customer';
            this.isCustomer = isCustomer;

            // Sales + Trader share a quoting-focused bottom nav (Orders,
            // Pricing, Mockup Studio, Notes) with Chat + Email tucked into
            // the overflow sheet.
            this.usesQuotingLayout = isSales || isTrader || isBusinessAssistant;
            // Admin + Production share an operations-focused bottom nav
            // (Orders, Delivery, Chat, Notes) with Email, Pricing, Design
            // Studio and Sign Out in the overflow sheet.
            this.usesOpsLayout = isAdmin || isProduction;

            // Delivery is for the roles that actually fulfill orders:
            // Admin and Production. Sales / Traders quote and sell but
            // don't run deliveries, so the tab is hidden for them.
            // Customers never see Delivery.
            this.showDeliveryTab = isAdmin || isProduction;
            // Pricing is a quoting tool: Admin / Sales / Trader. Customers
            // also get it so they can price their own jobs on the go.
            // Production gets it too (surfaced via the ops overflow sheet).
            this.showPricingTab =
                isAdmin ||
                isSales ||
                isTrader ||
                isBusinessAssistant ||
                isCustomer ||
                isProduction;
            this.showAiQuoteTab =
                isAdmin ||
                isSales ||
                isTrader ||
                isAssistant;
            // Quotes & Notes is an internal Sales companion to Pricing,
            // so it stays hidden from customers.
            this.showQuotesTab = isSales || isTrader || isBusinessAssistant;
            // Design Studio (Mockup) is offered to the quoting roles and
            // to customers so they can generate mockups themselves.
            // Production gets it too (surfaced via the ops overflow sheet).
            this.showMockupTab =
                isAdmin ||
                isSales ||
                isTrader ||
                isBusinessAssistant ||
                isCustomer ||
                isProduction;
            // Email is internal-only and requires a Nylas grant.
            this.showEmailTab = !isCustomer && !!userData?.email_token;
        } catch (e) {
            this.showDeliveryTab = false;
            this.showPricingTab = false;
            this.showAiQuoteTab = false;
            this.showQuotesTab = false;
            this.showMockupTab = false;
            this.showEmailTab = false;
            this.isCustomer = false;
            this.usesQuotingLayout = false;
            this.usesOpsLayout = false;
        }

        // Start the delivery-badge poller for users who actually see
        // the Delivery tab. Roles without the tab never trigger the
        // network calls — pointless work and a wasted log entry.
        if (this.showDeliveryTab) {
            this._startDeliveryCountPolling();
        }
    }

    /**
     * Fetch the pending-deliveries count once and then refresh on a
     * 60-second cadence. The Delivery page uses the same paginated
     * `getDeliveryOrders` endpoint and exposes `total` in the response,
     * so we read that here for the badge instead of inventing a new
     * count endpoint.
     */
    private _startDeliveryCountPolling(): void {
        this._loadDeliveryCount();
        if (isPlatformBrowser(this._platformId)) {
            this._deliveryPollTimer = setInterval(
                () => this._loadDeliveryCount(),
                60_000,
            );
        }
    }

    private _loadDeliveryCount(): void {
        this._ordersService
            .getDeliveryOrders(1)
            .pipe(take(1), takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    // `total` is the server-side count for this query
                    // (deliveries waiting to go out). The Delivery page
                    // applies a small client-side filter to hide
                    // already-delivered rows on the first page, so the
                    // badge can overcount by 1–2 in flight, which is
                    // fine for a glanceable indicator.
                    const total = Number(response?.total ?? 0);
                    this.pendingDeliveryCount = Number.isFinite(total)
                        ? total
                        : 0;
                },
                error: (err) => {
                    console.warn(
                        '[MobileLayout] Failed to refresh delivery count:',
                        err,
                    );
                },
            });
    }

    ngOnDestroy(): void {
        this._mailboxService.stopUnreadCountPolling();

        if (this._deliveryPollTimer) {
            clearInterval(this._deliveryPollTimer);
            this._deliveryPollTimer = null;
        }

        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        // Cleanup keyboard detection
        if (this._viewportResizeHandler && isPlatformBrowser(this._platformId)) {
            window.visualViewport?.removeEventListener('resize', this._viewportResizeHandler);
        }
    }

    /**
     * Setup keyboard detection using visualViewport API
     * When keyboard opens, viewport height decreases significantly
     */
    private _setupKeyboardDetection(): void {
        if (!isPlatformBrowser(this._platformId)) return;
        
        // Store initial viewport height
        this._initialViewportHeight = window.visualViewport?.height || window.innerHeight;
        
        if (window.visualViewport) {
            this._viewportResizeHandler = () => {
                const currentHeight = window.visualViewport!.height;
                const heightDiff = this._initialViewportHeight - currentHeight;
                
                // If viewport shrunk by more than 150px, keyboard is probably open
                const keyboardOpen = heightDiff > 150;
                
                if (keyboardOpen !== this.isKeyboardOpen) {
                    this.isKeyboardOpen = keyboardOpen;
                    
                    if (keyboardOpen) {
                        this._renderer.addClass(document.body, 'keyboard-open');
                    } else {
                        this._renderer.removeClass(document.body, 'keyboard-open');
                    }
                }
            };
            
            window.visualViewport.addEventListener('resize', this._viewportResizeHandler);
        }
    }

    /**
     * Navigate to orders tab
     */
    goToOrders(): void {
        this._router.navigate(['/m/orders']);
    }

    /**
     * Navigate to delivery tab
     */
    goToDelivery(): void {
        this._router.navigate(['/m/delivery']);
    }

    /**
     * Navigate to chat tab
     */
    goToChat(): void {
        this._router.navigate(['/m/chat']);
    }

    /**
     * Navigate to email tab
     */
    goToEmail(): void {
        this._router.navigate(['/m/email']);
    }

    /**
     * Navigate to pricing tab (mobile quote builder)
     */
    goToPricing(): void {
        this._router.navigate(['/m/pricing']);
    }

    /** Open the isolated, voice-first quote conversation. */
    goToAiQuote(): void {
        this._router.navigate(['/m/pricing/voice']);
    }

    /**
     * Navigate to the Quotes & Notes tab (saved pricing quotes + notes)
     */
    goToQuotes(): void {
        this._router.navigate(['/m/quotes']);
    }

    /**
     * Navigate to the Mockup Studio (Gemini mockup generator). Opened
     * from the overflow sheet rather than a bottom tab.
     */
    goToMockup(): void {
        this._router.navigate(['/m/mockup']);
    }

    /**
     * Open the standalone "New Note" editor. Available to every mobile
     * user from the overflow sheet — the note-taking tool was previously
     * buried inside Chat even though a note isn't a conversation.
     */
    goToNewNote(): void {
        this._router.navigate(['/m/notes/new']);
    }

    /**
     * Update active tab based on URL
     */
    private _updateActiveTab(url: string): void {
        if (url.includes('/m/chat')) {
            this.activeTab = 'chat';
        } else if (url.includes('/m/email')) {
            this.activeTab = 'email';
        } else if (url.includes('/m/delivery')) {
            this.activeTab = 'delivery';
        } else if (url.includes('/m/pricing/voice')) {
            this.activeTab = 'aiQuote';
        } else if (url.includes('/m/pricing')) {
            // Both /m/pricing and /m/pricing/send keep the Pricing tab
            // highlighted - the send screen is a sub-step of the same
            // flow from the user's perspective.
            this.activeTab = 'pricing';
        } else if (url.includes('/m/quotes')) {
            this.activeTab = 'quotes';
        } else if (url.includes('/m/mockup')) {
            this.activeTab = 'mockup';
        } else if (url.includes('/m/notes')) {
            this.activeTab = 'notes';
        } else {
            this.activeTab = 'orders';
        }
    }

    /**
     * Get formatted unread count for badge
     */
    get formattedUnreadCount(): string {
        if (this.unreadChatCount > 99) {
            return '99+';
        }
        return this.unreadChatCount.toString();
    }

    /**
     * Formatted pending-delivery count for the Delivery tab badge.
     * Mirrors the chat / email badge cap so 100+ pending deliveries
     * still fit visually inside the small mat-badge bubble.
     */
    get formattedDeliveryCount(): string {
        if (this.pendingDeliveryCount > 99) {
            return '99+';
        }
        return this.pendingDeliveryCount.toString();
    }

    /**
     * Trigger PWA install prompt
     */
    async installPwa(): Promise<void> {
        if (!this.deferredPrompt) {
            console.log('📱 No install prompt available');
            return;
        }

        // Show the install prompt
        this.deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await this.deferredPrompt.userChoice;
        console.log(`📱 User response to install prompt: ${outcome}`);

        // Clear the deferred prompt
        this.deferredPrompt = null;
        this.showInstallButton = false;
    }

    /**
     * Dismiss install prompt
     */
    dismissInstall(): void {
        this.showInstallButton = false;
        // Store in localStorage to not show again this session
        localStorage.setItem('pwa-install-dismissed', 'true');
    }

    /**
     * Sign out the user
     */
    signOut(): void {
        this._authService.signOut().subscribe({
            next: () => { window.location.href = '/sign-in'; },
            error: () => { window.location.href = '/sign-in'; },
        });
    }

}
